import { capitalizeString } from "@utils/utils";
import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

import { Collapsible, Small } from "@fronterahealth/frontera-ui-components";

import { ApiReportTemplateReportTypeChoices, ReportPageLocationEnums } from "@api/graphql/types-and-hooks";
import { useScrollToAnchor } from "@pages/ClinicalReportDetails/Components/StickyNav/useScrollToAnchor";
import {
  DiagnosisEvaluationPageLocations,
  InitialAbPageLocations,
  SinglePage,
} from "@pages/Templates/components/TemplateStructure/TemplateStructure";
import { getPageLocations } from "@pages/Templates/utils";
import { ReportTemplateSection } from "@providers/TemplateEditorProvider";

interface HeadingItemInterface {
  heading: string;
  hash: string;
}

const HeadingItem: React.FC<HeadingItemInterface> = ({ heading, hash }) => {
  const split = heading?.split(/-(.+)/); // split by first occurrence of "-"
  const actualHeading = split.length > 1 ? split[1] : "";

  const active = hash.replace("#", "") === heading;
  const headingLabel = actualHeading
    .split("-")
    .map((h) => capitalizeString(h))
    .join(" ");

  return (
    <Link to={`#${heading}`} key={heading}>
      <div className={`transition-all cursor-pointer py-2 pl-2 min-h-9`}>
        <div className="flex items-center">
          <Small
            className={`transition-all hover:text-text-primary`}
            displayType={active ? "loud" : "normal"}
            colorType={active ? "primary" : "secondary"}
          >
            {headingLabel}
          </Small>
        </div>
      </div>
    </Link>
  );
};

interface StickyNavProps {
  sections: ReportTemplateSection[];
  reportType: ApiReportTemplateReportTypeChoices;
}

/**
 * This is the floating nav item bar that reads and scans for any div elements with the "data-sticky-nav-header" data attribute
 * on them and renders them as clickable legend elements that will jump the user to that section
 * @returns
 */
export const StickyNav: React.FC<StickyNavProps> = ({ sections, reportType }) => {
  const { hash } = useLocation();
  const [headings, setHeadings] = React.useState<string[]>([]);

  useEffect(() => {
    let tries = 0;
    const maxTries = 10;
    const interval = 100; // ms
    let timeoutId: NodeJS.Timeout;

    const checkHeadings = () => {
      const _headings = Array.from(document.querySelectorAll(`[data-sticky-nav-header]`))
        .filter((div) => !div.id.endsWith("-placeholder"))
        .map((div) => div.id);

      if (_headings.length > 0 || tries >= maxTries) {
        setHeadings(_headings);
      } else {
        tries += 1;
        timeoutId = setTimeout(checkHeadings, interval);
      }
    };

    checkHeadings();

    return () => {
      clearTimeout(timeoutId);
    };
  }, [sections]);

  useScrollToAnchor();

  const groupedHeadings = React.useMemo(() => {
    const grouped: Record<string, Record<string, string[]>> = {};

    sections.forEach((section) => {
      // Use actual pageLocation for multi-page reports, SinglePage for others
      const location =
        reportType === ApiReportTemplateReportTypeChoices.InitialAssessment ||
        reportType === ApiReportTemplateReportTypeChoices.DiagnosisEvaluation
          ? section.pageLocation
          : SinglePage;

      if (!grouped[location]) {
        grouped[location] = {};
      }

      const sectionHeading = headings.find((heading) => heading.startsWith(section.id.replace("-", "")));

      if (sectionHeading) {
        if (!grouped[location][sectionHeading]) {
          grouped[location][sectionHeading] = [];
        }
        section.fields.forEach((field) => {
          const heading = headings.find((heading) => heading.startsWith(field.id.replace("-", "")));
          if (heading) {
            grouped[location][sectionHeading].push(heading);
          }
        });
      }
    });

    const sectionHeadings = Object.keys(grouped).flatMap((pageLocation) => Object.keys(grouped[pageLocation]));
    return { sectionHeadings, grouped };
  }, [headings, sections, reportType]);

  const sortedPageLocations = getPageLocations(reportType);

  return (
    <>
      {sortedPageLocations.map((pageLocation) => {
        const pageHeadings = groupedHeadings.grouped[pageLocation];
        if (pageHeadings) {
          return (
            <div key={pageLocation}>
              {pageLocation !== SinglePage && (
                <div className="pr-4">
                  <Small
                    className={`transition-all text-text-primary bg-limestone-50 rounded-md px-2 py-1`}
                    displayType="loud"
                    colorType="primary"
                  >
                    {reportType === ApiReportTemplateReportTypeChoices.InitialAssessment
                      ? InitialAbPageLocations[pageLocation as ReportPageLocationEnums]
                      : reportType === ApiReportTemplateReportTypeChoices.DiagnosisEvaluation
                        ? DiagnosisEvaluationPageLocations[pageLocation as ReportPageLocationEnums]
                        : pageLocation}
                  </Small>
                </div>
              )}
              {Object.entries(pageHeadings).map(([sectionHeading, fieldHeadings]) => (
                <div key={sectionHeading}>
                  {fieldHeadings.length > 0 ? (
                    <Collapsible
                      defaultOpenState={false}
                      headerContent={
                        <div className="pr-12 pl-2 hover:bg-limestone-50 w-full mr-4 rounded-xs">
                          <HeadingItem heading={sectionHeading} hash={hash} />
                        </div>
                      }
                      headerIsClickable={true}
                      bodyContent={
                        <div>
                          {fieldHeadings.map((fieldHeading) => (
                            <div className="pr-4" key={fieldHeading}>
                              <div className="pl-4 hover:bg-limestone-50 rounded-xs">
                                <HeadingItem heading={fieldHeading} hash={hash} />
                              </div>
                            </div>
                          ))}
                        </div>
                      }
                    />
                  ) : (
                    <div className="pr-4 pt-2" key={sectionHeading}>
                      <div className="pl-2 hover:bg-limestone-50 rounded-xs">
                        <HeadingItem heading={sectionHeading} hash={hash} />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          );
        }
        return null;
      })}
    </>
  );
};
